import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { IView } from 'Framework';
import { EmailField, PasswordField, ScrollBar, Link, Dialog } from '@framework-controls/core';
import { strings } from 'src/localization';
import * as css from './Login.module.scss';
import { Customer, CreateAcctInp, UpdateApptCustInp, UpdateApptOpsInp, ascPageviewInput, ascCtaInteractionInput, ascFormEngagementInput, ascLoginInput, VehicleRec, ascElementConfigurationInput, ascRetailProcessInput, SpecData } from 'src/models';
import { ComponentMapIds } from '../map';
import SVPButton from '../SVPButtons/SVPButton';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { ViewMode } from 'src/enums';
import { IconRemove } from 'src/content/icons';
import { ascCtaInteraction, ascFormEngagement, ascLogin, ascPageview, getElementColor, ascElementConfiguration, ascRetailProcess } from 'src/helpers/EventHelper/EventHelper';
import { sendSdEvent, getProviderId } from 'src/helpers/ShiftDigitalHelper/ShiftDigitalHelper';

const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source +
    /@/.source +
    /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

export interface Iprops {
    viewMode: ViewMode;
    custInfo?: Customer;
    pushToMainPanes: (inputView: IView) => void;
    pushToLeftPane: (inputView: IView) => void;
    onCustUpdate: (cust: Customer) => void;
    onLogin: (cust: CreateAcctInp, profile: string, store: string, branch: string) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    errMsg?: string;
    clearError: () => void;
    profileNum: string;
    store: string;
    branch: string;
    theme: string;
    loadAppt: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    selectVehicle: (id: string) => void;
    getVehicles: () => void;
    fetchROs: (customerNum: string) => void;
    setViewMode: (viewMode: ViewMode) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    getCustAppts: (custID: string, profile: string, store: string, branch: string) => void;
    ClearTokenData: () => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    sendResetEmail: (email: string, nameId: string, profile: string, store: string, branch: string) => void;
    mainPaneViewOrder: IView[];
    toyLexLoginSent: boolean;
    setApptHistId: (id: string) => void;
    setToyLexLoginSent: (inp) => void;
    popFromMainPane: (id: string) => void;
    mfaEnabled: boolean;
    identityVerified: boolean;
    verifyingIdentity: boolean;
    locale: string;
    setVehSelReset: (reset: boolean) => void;
    specData?: SpecData;
    sessionId: string;
    lexusMCLaunched: string;
    toyotaMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDMCLaunched: string;
    gmMemberSearch: (email: string) => void;
    myGMRewardsLaunched?: string;
}

const Login = (props: Iprops) => {
    let initialEmail = props.custInfo ? props.custInfo!.emailAddress : '';
    let fieldCheck = false;
    try {
        const regCheck = initialEmail.search(/[*]/);
        if (regCheck !== -1) {
            fieldCheck = true;
        }
    } catch (e) {
        fieldCheck = true;
    }
    if (fieldCheck) {
        initialEmail = '';
    }

    const [password, setPassword] = useState('');
    const [email, setEmail] = useState(initialEmail);
    const [moveOn, setMoveOn] = useState(false);
    const [loginClicked, setLoginClicked] = useState(false);
    const [forgotCSS, setForgotCSS] = useState('');
    const [showForgotHelp, setShowForgotHelp] = useState<boolean>(false);
    const [resetEmailSent, setResetEmailSent] = useState<boolean>(false);
    const [pageViewEvent, setPageViewEvent] = useState(false);
    const [displaySentMessage, setDisplaySentMessage] = useState<boolean>(false);
    const mainViewPanes = props.mainPaneViewOrder.map((view): IView => ({ ...view }));
    const currentMainPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId;

    const isSDAnalyticsEnabled =
        props.specData?.toyotaTrackingFlag === 'Y' ||
        props.specData?.lexusTrackingFlag === 'Y' ||
        props.specData?.mbzTrackingFlag === 'Y' ||
        props.specData?.mazdaCATrackingFlag === 'Y' ||
        props.specData?.vwTrackingFlag === 'Y';

    const loginEventHandler = useCallback((error) => {
        const ascLoginInp: ascLoginInput = {
            elementText: strings.Login,
            elementType: 'form',
            eventAction: 'click',
            eventActionResult: error ? 'error' : 'next_step',
            pageType: 'service',
        };
        ascLogin(ascLoginInp, props.vehicles, props.selectedVehicle);
        const ascRetailProcessInp: ascRetailProcessInput = {
            pageType: 'service',
            flowStage: '2b',
            flowOutcome: 'proceed',
            flowName: 'appointment',
            elementTitle: 'Log In',
        };
        ascRetailProcess(ascRetailProcessInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    const tryAgainBtnHandler = useCallback((e: any) => {
        let elementText = strings.TryAgain;
        if (props.errMsg === '1006') {
            elementText = strings.Okay;
        }
        props.clearError();
        setMoveOn(false);
        setResetEmailSent(false);
        setDisplaySentMessage(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: elementText,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: elementText,
            elementType: 'popup',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.clearError, props.errMsg, props.vehicles, props.selectedVehicle]);

    const ResetBtnHandler = useCallback((e: any) => {
        props.clearError();
        setMoveOn(false);
        props.sendResetEmail(email, '', props.profileNum, props.store, props.branch);
        setResetEmailSent(true);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ResetPass,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.sendResetEmail, email, props.profileNum,
        props.store, props.branch, props.vehicles, props.selectedVehicle]);

    const closeHandler = useCallback((e: any) => {
        props.clearError();
        setMoveOn(false);
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.vehicles, props.selectedVehicle]);

    const SignUpBtnHandler = useCallback((e: any) => {
        props.clearError();
        setMoveOn(false);
        props.pushToMainPanes({
            viewId: ComponentMapIds.SignUp,
            component: ComponentMapIds.SignUp,
        });
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.SignUp,
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.SignUp,
            elementType: 'popup',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    }, [props.clearError, props.pushToMainPanes, props.vehicles, props.selectedVehicle]);

    const emailChangedHandler = useCallback((id: string, value: string) => {
        setEmail(value);
    }, []);

    const passwordChangedHandler = useCallback((id: string, value: string) => {
        setPassword(value);
    }, []);

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    // determines if we should display an error message and prevent moving on to next screen
    useEffect(() => {
        if (!props.errMsg) {
            if (props.custInfo && props.custInfo.firstName && props.custInfo.firstName !== '' && props.custInfo.login &&
            props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component === ComponentMapIds.Login) {
                const apptCustInp: UpdateApptCustInp = {
                    nameId: props.custInfo.nameId,
                    firstName: props.custInfo.firstName,
                    lastName: props.custInfo.lastName,
                    emailAddress: props.custInfo.emailAddress,
                    phoneNumber: props.custInfo.phoneNumber,
                    phoneType: props.custInfo.phoneType,
                    prefContact: props.custInfo.prefContact,
                    loggedIn: props.custInfo.login,
                };
                props.onAppCustUpdate(apptCustInp);
                setMoveOn(true);
                loginEventHandler('');

                if (isSDAnalyticsEnabled) {
                    sendSdEvent('scheduleServiceLogin', {
                        scheduleServiceSessionId: props.sessionId,
                        scheduleServiceProvider: getProviderId(props.specData, props.toyotaMCLaunched,
                            props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched),
                    });
                }
                if (!props.toyLexLoginSent) {
                    props.setToyLexLoginSent(true);
                }
            }
        } else {
            setMoveOn(false);
            loginEventHandler('error');
        }
    }, [props.custInfo, props.errMsg, props.onAppCustUpdate, props.toyLexLoginSent, props.setToyLexLoginSent,
        props.mainPaneViewOrder, loginEventHandler, props.specData, props.sessionId, props.toyotaMCLaunched,
        props.toyotaTDMCLaunched, props.lexusMCLaunched, props.lexusTDMCLaunched, isSDAnalyticsEnabled]);
    // This is the screen the user is on.
    const currentViewPanes = mainViewPanes[mainViewPanes.length - 1];
    // This is the ViewPane we were on before going to Login.
    const newViewPanes = mainViewPanes[mainViewPanes.length - 2];

    useEffect(() => {
        if (!props.errMsg && props.verifyingIdentity && !props.identityVerified) {
            setMoveOn(true);
        }
        if (props.custInfo?.login && props.identityVerified) {
            setMoveOn(true);
        }
    }, [
        props.identityVerified,
        props.verifyingIdentity,
        props.errMsg,
        props.custInfo?.login,
        props.custInfo,
    ]);

    useEffect(() => {
        if (currentMainPane === ComponentMapIds.Login) {
            setPageViewEvent(true);
        }
    }, [currentMainPane]);

    useEffect(() => {
        if (pageViewEvent && props.errMsg === '') {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
            setPageViewEvent(false);
        }
    }, [pageViewEvent, props.errMsg, props.vehicles, props.selectedVehicle]);

    useEffect(() => {
        if (moveOn && props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component === ComponentMapIds.Login) {
            if (!props.mfaEnabled || props.identityVerified) {
                setPassword('');
                setEmail('');
            }
            if ((newViewPanes.component === ComponentMapIds.Summary || newViewPanes.component === ComponentMapIds.ServiceTab ||
                newViewPanes.component === ComponentMapIds.CalendarSelect ||
                newViewPanes.component === ComponentMapIds.AdditionalServices ||
                newViewPanes.component === ComponentMapIds.AddSkipServices ||
                newViewPanes.component === ComponentMapIds.AddVehicle ||
                newViewPanes.component === ComponentMapIds.QuickTime) && (props.identityVerified || !props.mfaEnabled)) {
                props.popFromMainPane(currentViewPanes.viewId);
                // If we have a selected vehicle we need to correct the logged id from the Login call
                if (props.selectedVehicle) {
                    props.setApptHistId(props.selectedVehicle);
                }
                // Set VehSel Reset Flag so that when we go back to VehSel,
                // we redo the log's vehicle list and sync the front and back end
                props.setVehSelReset(true);

                return;
            }
            switch (props.viewMode) {
                case ViewMode.CustAcct:
                    props.getVehicles();
                    if (props.custInfo && props.custInfo.vehicleList) {
                        const vehicles = props.custInfo.vehicleList;
                        if (vehicles.length === 1) {
                            props.selectVehicle(vehicles[0].id);
                        }
                    }
                    if (!props.mfaEnabled || props.identityVerified) {
                        props.pushToMainPanes({
                            viewId: ComponentMapIds.UserAccountMenu,
                            component: ComponentMapIds.UserAccountMenu,
                        });
                    } else {
                        if (props.verifyingIdentity) {
                            props.pushToMainPanes({
                                viewId: ComponentMapIds.VerifyIdentity,
                                component: ComponentMapIds.VerifyIdentity,
                            });
                        }
                    }
                    break;

                case ViewMode.Repair:
                    props.getVehicles();
                    if (props.custInfo && props.custInfo.vehicleList && props.identityVerified) {
                        const vehicles = props.custInfo.vehicleList;
                        if (vehicles.length === 1) {
                            props.selectVehicle(vehicles[0].id);
                        }
                    }
                    if (props.custInfo && (props.identityVerified || !props.mfaEnabled)) {
                        props.fetchROs(props.custInfo.nameId);
                        // Need to get customer appointment list as we skip over the userAccountMenu
                        props.getCustAppts(props.custInfo.nameId, props.profileNum, props.store, props.branch);
                    }
                    if (!props.identityVerified && props.mfaEnabled) {
                        props.pushToMainPanes({
                            viewId: ComponentMapIds.VerifyIdentity,
                            component: ComponentMapIds.VerifyIdentity,
                        });
                    } else {
                        props.setViewMode(ViewMode.CustAcct);
                        props.pushToMainPanes({
                            viewId: ComponentMapIds.OpenRepairSelect,
                            component: ComponentMapIds.OpenRepairSelect,
                        });
                    }
                    break;

                case ViewMode.Appt:
                default:
                    if (props.loadAppt && (props.identityVerified || !props.mfaEnabled)) {
                        // props.setViewMode(ViewMode.CustAcct);
                        props.setViewMode(ViewMode.EditAppt);

                        props.pushToMainPanes({
                            viewId: ComponentMapIds.Summary,
                            component: ComponentMapIds.Summary,
                        });
                        break;
                    }
                    if (props.custInfo && props.custInfo.vehicleList && (props.identityVerified || !props.mfaEnabled)) {
                        const vehicles = props.custInfo.vehicleList;
                        if (vehicles.length === 0) {
                            props.pushToMainPanes({
                                viewId: ComponentMapIds.AddVehicle,
                                component: ComponentMapIds.AddVehicle,
                            });
                            props.pushToLeftPane({
                                viewId: ComponentMapIds.PropertyOptions,
                                component: ComponentMapIds.PropertyOptions,
                            });
                        } else {
                            props.pushToMainPanes({
                                viewId: ComponentMapIds.VehSel,
                                component: ComponentMapIds.VehSel,
                            });
                        }
                    } else {
                        if (props.verifyingIdentity) {
                            props.pushToMainPanes({
                                viewId: ComponentMapIds.VerifyIdentity,
                                component: ComponentMapIds.VerifyIdentity,
                            });
                        }
                    }
            }
            setMoveOn(false);
            // If we have a selected vehicle we need to correct the logged id from Login
            if (props.selectedVehicle) {
                props.setApptHistId(props.selectedVehicle);
            }
            // Don't clear the ops if we are verifying identity
            if (!props.verifyingIdentity) {
                props.UpdateApptOps([]);
                props.ClearTokenData();
            }
        }
    }, [props.mainPaneViewOrder, props.ClearTokenData, props.UpdateApptOps, props.pushToMainPanes, props.custInfo, props.errMsg,
        props.loadAppt, props.branch, props.store, props.profileNum, props.fetchROs, props.getCustAppts, props.verifyingIdentity,
        props.getVehicles, props.selectVehicle, props.viewMode, props.setViewMode, props.popFromMainPane, props.identityVerified,
        moveOn, password, passwordChangedHandler, props.pushToLeftPane, currentViewPanes, newViewPanes, props.mfaEnabled,
        props.selectedVehicle, props.setApptHistId, props.setVehSelReset]);

    const loginHandler = useCallback((e?: any) => {
        if (password.length < 6 || !email.match(emailRegex)) {
            return;
        }
        const loginInfo = { firstName: '', lastName: '', phoneNumber: '', emailAddress: email, pwd: password };
        props.setToyLexLoginSent(false);
        props.onLogin(loginInfo, props.profileNum, props.store, props.branch);
        // setLoginClicked(true);
        if (e) {
            const ascCtaInteractionInp : ascCtaInteractionInput = {
                pageType: 'service',
                elementText: strings.LogIn,
                elementColor: '',
                elementOrder: '',
                elementType: 'form',
                elementSubType: 'nav_button',
                eventAction: 'click',
                eventActionResult: 'next_step',
                linkUrl: '',
            };
            ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
            const ascFormEngagementInp: ascFormEngagementInput = {
                pageType: 'service',
                commType: 'form',
                commStatus: 'engage',
                elementText: strings.Login,
                department: 'service',
                formName: 'Service_Portal_2',
                formType: 'service_appointment',
            };
            ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
        }
        setLoginClicked(true);
    }, [props.onLogin, props.profileNum, props.store, props.branch, email,
        password, props.setToyLexLoginSent, props.vehicles, props.selectedVehicle]);

    const signUpHandler = useCallback((e: any) => {
        props.pushToMainPanes({
            viewId: ComponentMapIds.SignUp,
            component: ComponentMapIds.SignUp,
        });
        setMoveOn(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.SignUp,
            elementColor: '',
            elementOrder: '',
            elementType: 'form',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.pushToMainPanes, props.vehicles, props.selectedVehicle]);

    const onBlurHandler = useCallback((id) => {
        let elementText = '';
        switch (id) {
            case 'email':
                elementText = strings.EmailAddress;
                break;
            case 'password':
                elementText = strings.Password;
                break;
            default:
        }
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: elementText,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    const error = useMemo(() => {
        if (props.errMsg && props.errMsg !== '0000' &&
        props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].component === ComponentMapIds.Login) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);

            return GetErrorMessage(props.errMsg, tryAgainBtnHandler, ResetBtnHandler, SignUpBtnHandler, closeHandler, email);
        }

        return null;
    }, [props.mainPaneViewOrder, props.errMsg, tryAgainBtnHandler, ResetBtnHandler,
        SignUpBtnHandler, closeHandler, email, props.vehicles, props.selectedVehicle]);

    // Code below commented out until the email and password fields have given the
    // onKeyPress prop.
    const keyFunction = useCallback((e) => {
        if (e.charCode === 13) {
            loginHandler(e);
        }
    }, [loginHandler]);

    let bodycss = css.body;
    if (props.viewMode === ViewMode.CustAcct) {
        bodycss = css.CustAcctBody;
    }

    useEffect(() => {
        setForgotCSS(css.passLit);
        if (props.theme === 'colorSensitive') {
            setForgotCSS(css.passLitAccess);
        }
        if (props.theme === 'bmw') {
            setForgotCSS(css.passLitBMW);
        }
    }, [props.theme, setForgotCSS, forgotCSS]);

    useEffect(() => {
        // This action is meant to be triggered after we were on the
        // verify identity screen, and we successfully verified the identity
        // Right now this is triggered if the user logs out then logs back in
        if (props.identityVerified && props.mfaEnabled && props.verifyingIdentity) {
            loginHandler();
        }
    }, [loginHandler, props.identityVerified, props.mfaEnabled, props.verifyingIdentity, email, password]);

    useEffect(() => {
        if (loginClicked && props.myGMRewardsLaunched === 'Y') {
            props.gmMemberSearch(email);
            setLoginClicked(false);
        }
    }, [loginHandler, email, props.gmMemberSearch, loginClicked, props.myGMRewardsLaunched]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Log In', '');
    }, [props.addLogEntry]);

    const forgotHandler = useCallback(() => {
        if (!email.match(emailRegex)) {
            setShowForgotHelp(true);
        } else {
            setShowForgotHelp(false);
            // send the forgot password email to the typed in email and notify the user
            props.sendResetEmail(email, '', props.profileNum, props.store, props.branch);
            setResetEmailSent(true);
        }
    }, [email, props.sendResetEmail, props.profileNum, props.store, props.branch]);

    const forgotEventHandler = useCallback((e: any) => {
        let actionResult = 'popup';
        if (!email.match(emailRegex)) {
            actionResult = 'error';
        }
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ForgotPassword,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'search_tool',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: actionResult,
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [email, props.vehicles, props.selectedVehicle]);

    // check the reset password email error and if it's sent to display the Dialog
    useEffect(() => {
        if (props.errMsg === '0000' && resetEmailSent) {
            setDisplaySentMessage(true);
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        } else {
            setDisplaySentMessage(false);
        }
    }, [props.errMsg, resetEmailSent, props.vehicles, props.selectedVehicle]);


    // The link will allow the user to edit the fields after it is selected.
    const forgotLink: React.ReactElement = (
        <Link
            className={`${forgotCSS} ${props.theme !== 'chevrolet' ? css.underline : ''}`}
            key={0} id={'Forgot'}
            onClick={forgotHandler}
        >
            <div onClick={forgotEventHandler}>{strings.ForgotPassword}</div>
        </Link>
    );

    const resetCloseHandler = useCallback((e: any) => {
        props.clearError();
        setDisplaySentMessage(false);
        setResetEmailSent(false);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'popup',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'close',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.vehicles, props.selectedVehicle]);

    // Reset Email Sent Dialog
    const resetSentMessage = <Dialog
        id='ResetEmailSent'
        cssClasses={{
            content: css.content,
            paper: css.paper,
            header: css.LightBoxHeader,
        }}
        isOpen={displaySentMessage}
        anchorOrigin={{
            vertical: 'top',
        }}
        header={strings.EmailSent}
        hideSave={true}
        hideCancel={true}
        disableUpperCase={true}
        onCancel={resetCloseHandler}
        onBackDropClick={resetCloseHandler}
        width={'full'}>
        <div>
            <div className={css.headerbox}>
                {strings.EmailSent}
                <div className={css.headerIcon}>
                    <IconRemove id='closeBtn' onClick={resetCloseHandler}/>
                </div>
            </div>
            {strings.formatString(strings.ResetPassEmailSent, email).toString()}
            <hr className={css.hrLightBox}/>
            <div className={css.btnArea}>
                <SVPButton
                    id='Proceed'
                    handler={resetCloseHandler}
                    message={strings.Okay}
                    noArrow
                    variant={'primaryBtnAlt'}
                    theme={props.theme}
                />
            </div>
        </div>
    </Dialog>;

    return (
        <ScrollBar scrollVertical>
            {resetSentMessage}
            <div className={bodycss}>
                {error}
                <div className={css.topContainer}>
                    <h2>{strings.LogIn}</h2>
                    <h3>{strings.EnterEmailPassword}</h3>
                    <hr />
                </div>
                <div className={css.bottomContainer} >
                    <span>{strings.RequiredFields} </span>
                    <div className={css.Input}>
                        <EmailField
                            id='email'
                            label={strings.EmailAddress}
                            value={email}
                            size='full'
                            onChange={emailChangedHandler}
                            onKeyPress={(e) => keyFunction(e)}
                            onBlur={onBlurHandler}
                        />
                    </div>
                    <div className={css.Input2}>
                        <PasswordField
                            id='password'
                            label={strings.Password}
                            value={password}
                            size='full'
                            onChange={passwordChangedHandler}
                            minLength={6}
                            maxLength={10}
                            onKeyPress={(e) => keyFunction(e)}
                            onBlur={onBlurHandler}
                        />
                    </div>
                    <div className={css.loginDiv}>
                        {/* <div className={css.passLit}> */}
                        <div className={forgotCSS}>
                            {/* Link for forgetting password */}
                            {/* {strings.ForgotPassword} */}
                            {forgotLink}
                            <div className={css.forgotHelp}>
                                {showForgotHelp? strings.PleaseEnterEmail: null}
                            </div>
                        </div>
                        <div className={css.passBtnDiv}>
                            <SVPButton
                                id='loginBtn'
                                variant={'primaryBtnAlt2'}
                                message={strings.LogIn}
                                noArrow={true}
                                handler={loginHandler}
                                tooltip={strings.LogInTitle}
                                isDisabled={!email.match(emailRegex) || password.length < 6}
                                theme={props.theme}
                            />
                        </div>
                    </div>
                    <div>
                        <hr />
                    </div>
                    <div className={css.signUpDiv}>
                        <div className={css.signUpLit}>
                            {strings.NoAccount}
                        </div>
                        <div className={css.btnDiv}>
                            <SVPButton
                                id='signUpBtn'
                                variant={'secondaryBtnAlt'}
                                message={strings.SignUp}
                                noArrow={true}
                                handler={signUpHandler}
                                tooltip={strings.CreateAcctTip}
                                theme={props.theme}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </ScrollBar>
    );
};

function GetErrorMessage(
    message: string,
    tryAgainHandler: (e: any) => void,
    resetHandler: (e: any) => void,
    signUpHandler: (e: any) => void,
    closeHandler: (e: any) => void,
    email: string) {
    let headerMsg = '';
    let errorMsg = '';
    let primaryMessage = '';
    let secondaryMessage = '';
    let primaryHandler = tryAgainHandler;
    let secondaryHandler = resetHandler;

    switch (message) {
        case '1001':
        default:
            headerMsg = strings.IncorrectPassword;
            errorMsg = strings.IncorrectPassMsg;
            primaryMessage = strings.TryAgain;
            secondaryMessage = strings.ResetPass;
            primaryHandler = tryAgainHandler;
            secondaryHandler = resetHandler;
            break;

        case '1002':
            headerMsg = strings.NoAccountFound;
            errorMsg = strings.formatString(strings.NoAccFoundMsg, email).toString();
            primaryMessage = strings.TryAgain;
            secondaryMessage = strings.SignUp;
            primaryHandler = tryAgainHandler;
            secondaryHandler = signUpHandler;
            break;

        case '1005':
            headerMsg = strings.NoAccountFound;
            errorMsg = strings.formatString(strings.NoAccFoundMsg, email).toString();
            primaryMessage = strings.TryAgain;
            secondaryMessage = strings.SignUp;
            primaryHandler = tryAgainHandler;
            secondaryHandler = signUpHandler;
            break;

        case '1006':
            headerMsg = strings.ContactDealer;
            errorMsg = strings.ResetPasswordContactDeal;
            primaryMessage = strings.Okay;
            secondaryMessage = '';
            primaryHandler = tryAgainHandler;
            secondaryHandler = tryAgainHandler;
            break;
    }

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={secondaryMessage}
            primaryHandler={primaryHandler}
            secondaryHandler={secondaryHandler}
            closeHandler={closeHandler}
        />
    );
}

export default Login;